import { Routes } from '@angular/router';
import { LoginComponent } from './login/login.component';
import { HomeComponent } from './home/home.component';
import { DashboardComponent } from './dashboard/dashboard.component'; 
import { AccountsComponent } from './accounts/accounts.component';     
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Auth, signOut, User } from '@angular/fire/auth';
import { onAuthStateChanged } from 'firebase/auth';
import { Observable, of } from 'rxjs';
import { map, take, switchMap } from 'rxjs/operators';
import { AccessService } from './services/access.service';
import { CompanyManagementComponent } from './company-management/company-management.component';
import { InvoiceComponent } from './invoice/invoice.component';
import { OwnerManagementComponent } from './owner/owner-management.component';
import { StoreManagementComponent } from './store/store-management.component';
import { DevToolComponent } from './dev-tool/dev-tool.component';
import { ProfitReportComponent } from './profit-report/profit-report.component';
import { AccessControlComponent } from './access-control/access-control.component';
import { roleGuard } from './guards/role.guard';
import { UnauthorizedComponent } from './access-control/unauthorized.component';
import { DailyReportComponent } from './daily-report/daily-report.component';
import { FrameReportComponent } from './frame-report/frame-report.component';
import { SmrsFeeReportComponent } from './smre-fee-report/smrs-fee-report.component';
import { SalesInvoiceComponent } from './invoice/sales-invoice/sales-invoice.component';
import { SmrsRateManagementComponent } from './smrs-rate-management/smrs-rate-management.component';
import { SalesReportComponent } from './sales-report/sales-report.component';
import { BankManagementComponent } from './bank-management/bank-management.component';
import { AgentManagementComponent } from './agent-management/agent-management.component';
import { ArtistManagementComponent } from './artist-management/artist-management.component';
import { CouponManagementComponent } from './coupon-management/coupon-management.component';
import { ArtistStatementComponent } from './artist-statement/artist-statement.component';
import { BrandManagementComponent } from './brand-management/brand-management.component';
import { PhotoboothManagementComponent } from './photobooth-management/photobooth-management.component';
import { ThemeLayoutEditorComponent } from './photobooth-management/theme-layout-editor/theme-layout-editor.component';
import { PhotoboothConfigHubComponent } from './photobooth-management/photobooth-config-hub/photobooth-config-hub.component';
import { RefundManagementComponent } from './refund-management/refund-management.component';
import { KonoboxManagementComponent } from './konobox-management/konobox-management.component';


const authState = (auth: Auth): Observable<User | null> => new Observable<User | null>(subscriber => {
  return onAuthStateChanged(auth,
    user => subscriber.next(user),
    err => subscriber.error(err),
    () => subscriber.complete()
  );
});

export const redirectUnauthorizedToLogin = () => {
  const auth = inject(Auth);
  const router = inject(Router);
  const accessService = inject(AccessService);
  return authState(auth).pipe(
    take(1),
    switchMap(user => {
      if (!user) {
        return of(router.createUrlTree(['/login']));
      }
      return accessService.getUserProfile(user.uid).pipe(
        take(1),
        switchMap(profile => {
          if (!profile || !profile.role) return of(router.createUrlTree(['/login']));
          if (profile.role.toLowerCase() === 'user') {
            signOut(auth);
            return of(router.createUrlTree(['/login']));
          }

          return accessService.getRoles().pipe(
            take(1),
            map(roles => {
              const roleId = profile.roleId;
              const roleStr = profile.role?.toLowerCase().trim();
              const resolvedRole = roles.find(r => 
                (r.id && (r.id === roleId || r.id.toLowerCase() === roleStr)) ||
                (r.name && roleStr && r.name.toLowerCase().trim() === roleStr)
              );
              if (resolvedRole?.isCustomer) {
                signOut(auth);
                return router.createUrlTree(['/login']);
              }
              return true;
            })
          );
        })
      );
    })
  );
};

export const redirectLoggedInToHome = () => {
  const auth = inject(Auth);
  const router = inject(Router);
  const accessService = inject(AccessService);
  return authState(auth).pipe(
    take(1),
    switchMap(user => {
      if (!user) {
        return of(true);
      }
      return accessService.getUserProfile(user.uid).pipe(
        take(1),
        switchMap(profile => {
          if (!profile || !profile.role || profile.role.toLowerCase() === 'user') {
            signOut(auth);
            return of(true);
          }

          return accessService.getRoles().pipe(
            take(1),
            map(roles => {
              const roleId = profile.roleId;
              const roleStr = profile.role?.toLowerCase().trim();
              const resolvedRole = roles.find(r => 
                (r.id && (r.id === roleId || r.id.toLowerCase() === roleStr)) ||
                (r.name && roleStr && r.name.toLowerCase().trim() === roleStr)
              );
              if (resolvedRole?.isCustomer) {
                signOut(auth);
                return true;
              }
              return router.createUrlTree(['/home']);
            })
          );
        })
      );
    })
  );
};

export const routes: Routes = [
  { 
    path: '', 
    redirectTo: 'login', 
    pathMatch: 'full' 
  },
  { 
    path: 'login', 
    component: LoginComponent,
    canActivate: [redirectLoggedInToHome]
  },
  { 
    // 🟢 PARENT ROUTE (The Shell)
    path: 'home', 
    component: HomeComponent,
    canActivate: [redirectUnauthorizedToLogin],
    
    // 🟢 CHILDREN ROUTES (The Content)
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' }, // Default to dashboard
      { path: 'dashboard', component: DashboardComponent },
      { path: 'invoice', component: InvoiceComponent, canActivate: [roleGuard], data: { id: 'invoice' } },
      { path: 'sales-invoice', component: SalesInvoiceComponent, canActivate: [roleGuard], data: { id: 'sales-invoice' } },
      { path: 'accounts', component: AccountsComponent, canActivate: [roleGuard], data: { id: 'accounts' } },
      { path: 'company-management', component: CompanyManagementComponent, canActivate: [roleGuard], data: { id: 'company-management' } },
      { path: 'owner-management', component: OwnerManagementComponent, canActivate: [roleGuard], data: { id: 'owner-management' } },
      { path: 'store-management', component: StoreManagementComponent, canActivate: [roleGuard], data: { id: 'store-management' } },
      { path: 'profit-report', component: ProfitReportComponent, canActivate: [roleGuard], data: { id: 'profit-report' } },
      { path: 'daily-report', component: DailyReportComponent, canActivate: [roleGuard], data: { id: 'daily-report' } },
      { path: 'frame-report', component: FrameReportComponent, canActivate: [roleGuard], data: { id: 'frame-report' } },
      { path: 'smrs-fee-report', component: SmrsFeeReportComponent, canActivate: [roleGuard], data: { id: 'smrs-fee-report' } },
      { path: 'dev-tool', component: DevToolComponent, canActivate: [roleGuard], data: { id: 'dev-tool' } },
      { path: 'smrs-rate-management', component: SmrsRateManagementComponent, canActivate: [roleGuard], data: { id: 'smrs-rate-management' } },
      { path: 'sales-report', component: SalesReportComponent, canActivate: [roleGuard], data: { id: 'sales-report' } },
      { path: 'artist-statement', component: ArtistStatementComponent, canActivate: [roleGuard], data: { id: 'artist-statement' } },
      { path: 'bank-management', component: BankManagementComponent, canActivate: [roleGuard], data: { id: 'bank-management' } },
      { path: 'agent-management', component: AgentManagementComponent, canActivate: [roleGuard], data: { id: 'agent-management' } },
      { path: 'artist-management', component: ArtistManagementComponent, canActivate: [roleGuard], data: { id: 'artist-management' } },
      { path: 'coupon-management', component: CouponManagementComponent, canActivate: [roleGuard], data: { id: 'coupon-management' } },
      { path: 'refund-management', component: RefundManagementComponent, canActivate: [roleGuard], data: { id: 'refund-management' } },
      { path: 'brand-management', component: BrandManagementComponent, canActivate: [roleGuard], data: { id: 'brand-management' } },
      { path: 'photobooth-management', component: PhotoboothManagementComponent, canActivate: [roleGuard], data: { id: 'photobooth-management' } },
      { path: 'photobooth-management/config/:storeId/:deviceId', component: PhotoboothConfigHubComponent, canActivate: [roleGuard], data: { id: 'photobooth-management' } },
      { path: 'photobooth-management/layout-editor/:storeId/:deviceId', component: ThemeLayoutEditorComponent, canActivate: [roleGuard], data: { id: 'photobooth-management' } },
      { path: 'konobox-management', component: KonoboxManagementComponent, canActivate: [roleGuard], data: { id: 'konobox-management' } },
      { path: 'access-control', component: AccessControlComponent, canActivate: [roleGuard], data: { id: 'access-control' } },
      { path: 'unauthorized', component: UnauthorizedComponent }

    ]
  }
];
